<script setup>
import { ref, onMounted, computed, onBeforeUnmount } from "vue";
import SpendzenFloatingMenu from "@/components/spendzen/FloatingMenu.vue";
import { useRegion } from "@/composables/useRegion";
import ContactForm from "@/components/ContactForm.vue";

const { t, tm } = useI18n();
const localePath = useLocalePath();
const {
  currentRegion,
  setRegion,
  isSwiss,
  formatValueWithCurrency,
  countryNameKey,
  countryAdjKey,
} = useRegion();

const isMobileMenuOpen = ref(false);
const isSignupModalOpen = ref(false);
const signupStep = ref(1);
const signupEmail = ref("");

const faqsList = computed(() => [
  {
    question: t("spendzen.faq.q1.question"),
    answer: t("spendzen.faq.q1.answer"),
  },
  {
    question: t("spendzen.faq.q2.question"),
    answer: t("spendzen.faq.q2.answer"),
  },
  {
    question: t("spendzen.faq.q3.question"),
    answer: t("spendzen.faq.q3.answer"),
  },
  {
    question: t("spendzen.faq.q4.question"),
    answer: t("spendzen.faq.q4.answer"),
  },
  {
    question: t("spendzen.faq.q5.question"),
    answer: t("spendzen.faq.q5.answer"),
  },
  {
    question: t("spendzen.faq.q6.question"),
    answer: "",
  },
]);

definePageMeta({
  // layout: "default", // default is implicit
});

// SEO
useHead({
  title: t("spendzen.seo.title"),
  bodyAttrs: {
    class: "has-sidebar",
  },
  meta: [
    {
      name: "description",
      content: t("spendzen.seo.description"),
    },
    {
      property: "og:title",
      content: t("spendzen.seo.title"),
    },
    {
      property: "og:description",
      content: t("spendzen.seo.og_description"),
    },
    { property: "og:type", content: "website" },
  ],
  link: [
    { rel: "preconnect", href: "https://fonts.googleapis.com" },
    { rel: "preconnect", href: "https://fonts.gstatic.com", crossorigin: true },
    {
      href: "https://fonts.googleapis.com/css2?family=Geologica:wght@300;400;500;600;700;800&display=swap",
      rel: "stylesheet",
    },
  ],
});

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

const openSignupModal = () => {
  isSignupModalOpen.value = true;
  signupStep.value = 1;
  signupEmail.value = "";
  // Prevent body scroll? logic can be added if needed, but simple v-if/fixed overlay works
};

const closeSignupModal = () => {
  isSignupModalOpen.value = false;
};

const handleSignupSubmit = () => {
  // Simulate API call
  console.log("Signup request for:", signupEmail.value);
  signupStep.value = 2;
};

const resendEmail = () => {
  alert(`Email de vérification renvoyé à ${signupEmail.value}`);
};

// Testimonials carousel logic
const testimonials = computed(() => [
  {
    author: t("spendzen.testimonials.testimonial1.author"),
    role: t("spendzen.testimonials.testimonial1.role"),
    text: t("spendzen.testimonials.testimonial1.text"),
    initials: "ML",
  },
  {
    author: t("spendzen.testimonials.testimonial2.author"),
    role: t("spendzen.testimonials.testimonial2.role"),
    text: t("spendzen.testimonials.testimonial2.text"),
    initials: "PD",
  },
  {
    author: t("spendzen.testimonials.testimonial3.author"),
    role: t("spendzen.testimonials.testimonial3.role"),
    text: t("spendzen.testimonials.testimonial3.text"),
    initials: "SF",
  },
  {
    author: t("spendzen.testimonials.testimonial4.author"),
    role: t("spendzen.testimonials.testimonial4.role"),
    text: t("spendzen.testimonials.testimonial4.text"),
    initials: "JR",
  },
]);

const currentTestimonialPage = ref(0);
const testimonialPageSize = 3;

const totalTestimonialPages = computed(() =>
  Math.ceil(testimonials.value.length / testimonialPageSize),
);

const testimonialPages = computed(() => {
  const chunks = [];
  for (let i = 0; i < testimonials.value.length; i += testimonialPageSize) {
    chunks.push(testimonials.value.slice(i, i + testimonialPageSize));
  }
  return chunks;
});

const prevTestimonialPage = () => {
  currentTestimonialPage.value =
    (currentTestimonialPage.value - 1 + totalTestimonialPages.value) %
    totalTestimonialPages.value;
};

const nextTestimonialPage = () => {
  currentTestimonialPage.value =
    (currentTestimonialPage.value + 1) % totalTestimonialPages.value;
};

let testimonialInterval;
let touchStartX = 0;
let touchEndX = 0;

const handleTestimonialTouchStart = (e) => {
  touchStartX = e.changedTouches[0].screenX;
};

const handleTestimonialTouchEnd = (e) => {
  touchEndX = e.changedTouches[0].screenX;
  handleTestimonialSwipe();
};

const handleTestimonialSwipe = () => {
  const threshold = 50;
  const distance = touchEndX - touchStartX;
  if (Math.abs(distance) > threshold) {
    distance > 0 ? prevTestimonialPage() : nextTestimonialPage();
  }
};

// Intersection Observer for animations
onMounted(() => {
  const observerOptions = {
    threshold: 0.1,
    rootMargin: "0px 0px -50px 0px",
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add("animate-in");
      }
    });
  }, observerOptions);

  document
    .querySelectorAll(".animate-on-scroll, .animate-slide-left")
    .forEach((el) => {
      observer.observe(el);
    });

  // Auto-scroll testimonials
  testimonialInterval = setInterval(nextTestimonialPage, 6000);
});

onBeforeUnmount(() => {
  clearInterval(testimonialInterval);
});
</script>

<template>
  <div class="relative">
    <SpendzenFloatingMenu />

    <main class="space-y-40 mb-40 sidebar-shifted">
      <!-- Hero Section -->
      <section
        id="home"
        class="relative min-h-[calc(100vh-140px)] w-full flex items-center justify-center text-center overflow-hidden bg-white dark:bg-gray-900 pt-24"
      >
        <!-- Background gradient matching Kanzen -->
        <div
          class="absolute inset-0 z-0 bg-gradient-to-b from-transparent to-white dark:to-gray-900"
        ></div>

        <Container class="relative z-10">
          <div
            class="grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-16 items-center"
          >
            <!-- Text Content -->
            <div class="animate-fade-in-up text-left">
              <h1
                class="text-4xl md:text-5xl lg:text-7xl font-extrabold text-gray-900 dark:text-white leading-tight mb-6"
              >
                {{ t("spendzen.hero.title") }}<br /><span
                  class="gradient-text"
                  >{{ t("spendzen.hero.title_highlight") }}</span
                >
              </h1>
              <p class="text-lg text-gray-600 dark:text-gray-300 mb-8">
                {{ t("spendzen.hero.description") }}
                <strong class="text-gray-800 dark:text-gray-100">{{
                  t("spendzen.hero.description_highlight")
                }}</strong
                >{{ t("spendzen.hero.description_end") }}
              </p>

              <!-- Hero Buttons -->
              <div class="flex flex-row lg:flex-nowrap gap-4 mb-8">
                <AppButton
                  class="flex-1 lg:flex-none justify-center"
                  variant="black"
                  size="lg"
                  icon="ph:apple-logo-fill"
                  text="App Store"
                />

                <AppButton
                  class="flex-1 lg:flex-none justify-center"
                  variant="black"
                  size="lg"
                  icon="ph:google-play-logo-fill"
                  text="Google Play"
                />

                <AppButton
                  class="flex-1 lg:flex-none justify-center whitespace-nowrap"
                  @click="openSignupModal"
                  variant="outline"
                  size="lg"
                  :text="t('spendzen.hero.try_online')"
                  icon="ph:globe-bold"
                />
              </div>

              <div
                class="flex flex-wrap items-center gap-6 lg:gap-8 pt-8 border-t border-gray-100 dark:border-gray-800"
              >
                <div class="flex flex-col">
                  <span class="text-2xl font-bold text-primary">{{
                    t("spendzen.hero.free")
                  }}</span>
                  <span class="text-sm text-gray-500 dark:text-gray-400">{{
                    t("spendzen.hero.scans_per_month")
                  }}</span>
                </div>
                <div class="hidden sm:block w-px h-10 bg-gray-200"></div>
                <div class="flex flex-col">
                  <span class="text-2xl font-bold text-primary">98%</span>
                  <span class="text-sm text-gray-500 dark:text-gray-400">{{
                    t("spendzen.hero.ai_precision")
                  }}</span>
                </div>
                <div class="hidden sm:block w-px h-10 bg-gray-200"></div>
                <div class="flex flex-col">
                  <span class="text-2xl font-bold text-primary">{{
                    t("spendzen.hero.export")
                  }}</span>
                  <span class="text-sm text-gray-500 dark:text-gray-400">{{
                    t("spendzen.hero.ready_for_accountant")
                  }}</span>
                </div>
              </div>
            </div>

            <!-- Phone Mockup -->
            <div
              class="relative flex justify-center lg:justify-center animate-fade-in-up-delay lg:pl-10 pb-10 xl:pb-0"
            >
              <div
                class="relative w-[280px] xl:w-[320px] h-[600px] xl:h-[650px] bg-gradient-to-b from-gray-800 to-gray-900 rounded-[40px] p-3 phone-shadow flex flex-col"
              >
                <div
                  class="w-full h-full bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 rounded-[30px] p-5 overflow-hidden flex flex-col gap-3"
                >
                  <div class="flex justify-between items-center mb-2">
                    <span class="font-semibold text-gray-900 dark:text-white">{{
                      t("spendzen.phone_mockup.my_expenses")
                    }}</span>
                    <span
                      class="text-xs px-2 py-0.5 bg-primary text-white rounded-full"
                      >{{ t("spendzen.phone_mockup.scans_count") }}</span
                    >
                  </div>
                  <!-- Expense cards -->
                  <div
                    class="flex items-center gap-3 p-3 bg-white dark:bg-white/10 rounded-xl shadow-sm"
                  >
                    <div
                      class="w-10 h-10 bg-gray-50 dark:bg-gray-700 rounded-lg flex items-center justify-center text-xl"
                    >
                      🧾
                    </div>
                    <div class="flex-1">
                      <div
                        class="text-sm font-medium text-gray-800 dark:text-gray-100"
                      >
                        {{ t("spendzen.phone_mockup.restaurant_name") }}
                      </div>
                      <div class="text-xs text-gray-400">
                        {{ t("spendzen.phone_mockup.today") }}
                      </div>
                    </div>
                    <span
                      class="text-sm font-semibold text-gray-900 dark:text-white"
                      >{{ formatValueWithCurrency(45.8) }}</span
                    >
                  </div>
                  <div
                    class="flex items-center gap-3 p-3 bg-white dark:bg-white/10 rounded-xl shadow-sm"
                  >
                    <div
                      class="w-10 h-10 bg-gray-50 dark:bg-gray-700 rounded-lg flex items-center justify-center text-xl"
                    >
                      🚕
                    </div>
                    <div class="flex-1">
                      <div
                        class="text-sm font-medium text-gray-800 dark:text-gray-100"
                      >
                        {{ t("spendzen.phone_mockup.taxi_airport") }}
                      </div>
                      <div class="text-xs text-gray-400">
                        {{ t("spendzen.phone_mockup.yesterday") }}
                      </div>
                    </div>
                    <span
                      class="text-sm font-semibold text-gray-900 dark:text-white"
                      >{{ formatValueWithCurrency(62) }}</span
                    >
                  </div>
                  <div
                    class="flex items-center gap-3 p-3 bg-white dark:bg-white/10 rounded-xl shadow-sm border-l-4 border-green-500"
                  >
                    <div
                      class="w-10 h-10 bg-gray-50 dark:bg-gray-700 rounded-lg flex items-center justify-center text-xl"
                    >
                      📄
                    </div>
                    <div class="flex-1">
                      <div
                        class="text-sm font-medium text-gray-800 dark:text-gray-100"
                      >
                        {{ t("spendzen.phone_mockup.export_january") }}
                      </div>
                      <div class="text-xs text-green-600 font-medium">
                        {{ t("spendzen.phone_mockup.ready_to_send") }}
                      </div>
                    </div>
                    <span
                      class="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-1 rounded-full"
                      >PDF</span
                    >
                  </div>
                  <div
                    class="mt-auto flex items-center justify-center gap-2 p-4 btn-gradient text-white rounded-2xl font-semibold text-sm"
                  >
                    <span>📸</span>
                    {{ t("spendzen.phone_mockup.scan_receipt") }}
                  </div>
                </div>

                <!-- Floating cards (moved inside) -->
                <div
                  class="absolute top-[20%] -right-16 md:-right-20 flex items-center gap-3 px-4 py-3 bg-white dark:bg-gray-800 rounded-2xl shadow-glass animate-float z-20"
                >
                  <span class="text-xl">🤖</span>
                  <div class="flex flex-col whitespace-nowrap">
                    <span
                      class="text-sm font-semibold text-gray-900 dark:text-white"
                      >{{ t("spendzen.phone_mockup.ai_extraction") }}</span
                    >
                    <span class="text-xs text-gray-500 dark:text-gray-400">{{
                      t("spendzen.phone_mockup.data_extracted")
                    }}</span>
                  </div>
                </div>
                <div
                  class="absolute bottom-[25%] -left-4 md:-left-8 flex items-center gap-3 px-4 py-3 bg-white dark:bg-gray-800 rounded-2xl shadow-glass animate-float z-20"
                  style="animation-delay: 1.5s"
                >
                  <span class="text-xl">📧</span>
                  <div class="flex flex-col whitespace-nowrap">
                    <span
                      class="text-sm font-semibold text-gray-900 dark:text-white"
                      >{{ t("spendzen.phone_mockup.export_pdf") }}</span
                    >
                    <span class="text-xs text-gray-500 dark:text-gray-400">{{
                      t("spendzen.phone_mockup.ready_for_accountant")
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>

        <!-- Wave -->
        <div
          class="absolute bottom-0 left-0 right-0 leading-none z-0 pointer-events-none"
        >
          <svg
            viewBox="0 0 1440 120"
            preserveAspectRatio="none"
            class="w-full h-20"
          >
            <path
              d="M0,60 C360,120 720,0 1080,60 C1260,90 1380,60 1440,60 L1440,120 L0,120 Z"
              class="fill-white dark:fill-gray-900 transition-colors"
            />
          </svg>
        </div>
      </section>

      <!-- Features Section -->
      <section id="features" class="bg-white dark:bg-gray-900">
        <Container>
          <div class="text-center max-w-2xl mx-auto mb-16">
            <h2
              class="text-3xl md:text-5xl font-extrabold text-gray-900 dark:text-white mb-4 leading-tight"
            >
              {{ t("spendzen.features.title") }}<br /><span
                class="gradient-text"
                >{{ t("spendzen.features.title_highlight") }}</span
              >
            </h2>
            <p class="text-lg text-gray-500 dark:text-gray-400">
              {{ t("spendzen.features.subtitle") }}
            </p>
          </div>

          <div class="grid md:grid-cols-3 gap-8">
            <div
              class="animate-on-scroll relative p-8 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-3xl hover:border-primary/30 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
            >
              <div class="mb-6">
                <span
                  class="inline-flex items-center justify-center w-14 h-14 bg-primary/10 rounded-2xl text-2xl"
                  >📸</span
                >
              </div>
              <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-3">
                {{ t("spendzen.features.capture.title") }}
              </h3>
              <p class="text-gray-600 dark:text-gray-300 mb-6">
                {{ t("spendzen.features.capture.description") }}
              </p>
              <ul class="space-y-2">
                <li
                  class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300"
                >
                  <span class="text-primary font-bold">✓</span>
                  {{ t("spendzen.features.capture.feature1") }}
                </li>
                <li
                  class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300"
                >
                  <span class="text-primary font-bold">✓</span>
                  {{ t("spendzen.features.capture.feature2") }}
                </li>
                <li
                  class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300"
                >
                  <span class="text-primary font-bold">✓</span>
                  {{ t("spendzen.features.capture.feature3") }}
                </li>
              </ul>
            </div>

            <!-- Feature 2 -->
            <div
              class="animate-on-scroll relative p-8 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-3xl hover:border-primary/30 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              style="transition-delay: 100ms"
            >
              <div class="mb-6">
                <span
                  class="inline-flex items-center justify-center w-14 h-14 bg-primary/10 rounded-2xl text-2xl"
                  >⚡</span
                >
              </div>
              <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-3">
                {{ t("spendzen.features.extraction.title") }}
              </h3>
              <p class="text-gray-600 dark:text-gray-300 mb-6">
                {{ t("spendzen.features.extraction.description") }}
              </p>
              <ul class="space-y-2">
                <li
                  class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300"
                >
                  <span class="text-primary font-bold">✓</span>
                  {{ t("spendzen.features.extraction.feature1") }}
                </li>
                <li
                  class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300"
                >
                  <span class="text-primary font-bold">✓</span>
                  {{ t("spendzen.features.extraction.feature2") }}
                </li>
                <li
                  class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300"
                >
                  <span class="text-primary font-bold">✓</span>
                  {{ t("spendzen.features.extraction.feature3") }}
                </li>
              </ul>
            </div>

            <!-- Feature 3 -->
            <div
              class="animate-on-scroll relative p-8 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-3xl hover:border-primary/30 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              style="transition-delay: 200ms"
            >
              <div class="mb-6">
                <span
                  class="inline-flex items-center justify-center w-14 h-14 bg-primary/10 rounded-2xl text-2xl"
                  >📄</span
                >
              </div>
              <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-3">
                {{ t("spendzen.features.export.title") }}
              </h3>
              <p class="text-gray-600 dark:text-gray-300 mb-6">
                {{ t("spendzen.features.export.description") }}
              </p>
              <ul class="space-y-2">
                <li
                  class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300"
                >
                  <span class="text-primary font-bold">✓</span>
                  {{ t("spendzen.features.export.feature1") }}
                </li>
                <li
                  class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300"
                >
                  <span class="text-primary font-bold">✓</span>
                  {{ t("spendzen.features.export.feature2") }}
                </li>
                <li
                  class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300"
                >
                  <span class="text-primary font-bold">✓</span>
                  {{ t("spendzen.features.export.feature3") }}
                </li>
              </ul>
            </div>
          </div>
        </Container>
      </section>

      <section id="how-it-works" class="bg-white dark:bg-gray-900">
        <Container>
          <div class="text-center max-w-2xl mx-auto mb-16">
            <h2
              class="text-3xl md:text-5xl font-extrabold text-gray-900 dark:text-white mb-4 leading-tight"
            >
              {{ t("spendzen.how_it_works.title") }}<br /><span
                class="gradient-text"
                >{{ t("spendzen.how_it_works.title_highlight") }}</span
              >
            </h2>
            <p class="text-lg text-gray-500 dark:text-gray-400">
              {{ t("spendzen.how_it_works.subtitle") }}
            </p>
          </div>

          <div class="grid md:grid-cols-4 gap-8">
            <div class="animate-on-scroll relative text-center group">
              <div class="relative z-10 mb-6">
                <div
                  class="inline-flex items-center justify-center w-20 h-20 bg-primary text-white rounded-full text-3xl font-bold shadow-lg shadow-primary/30 group-hover:scale-110 transition-transform"
                >
                  1
                </div>
              </div>
              <div
                class="hidden md:block absolute top-10 left-[60%] w-full h-0.5 bg-gradient-to-r from-primary/50 to-transparent"
              ></div>
              <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-2">
                {{ t("spendzen.how_it_works.step1.title") }}
              </h3>
              <p class="text-gray-600 dark:text-gray-300 text-sm">
                {{ t("spendzen.how_it_works.step1.description") }}
              </p>
              <div class="mt-4 text-4xl">📸</div>
            </div>

            <div
              class="animate-on-scroll relative text-center group"
              style="transition-delay: 100ms"
            >
              <div class="relative z-10 mb-6">
                <div
                  class="inline-flex items-center justify-center w-20 h-20 bg-primary text-white rounded-full text-3xl font-bold shadow-lg shadow-primary/30 group-hover:scale-110 transition-transform"
                >
                  2
                </div>
              </div>
              <div
                class="hidden md:block absolute top-10 left-[60%] w-full h-0.5 bg-gradient-to-r from-primary/50 to-transparent"
              ></div>
              <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-2">
                {{ t("spendzen.how_it_works.step2.title") }}
              </h3>
              <p class="text-gray-600 dark:text-gray-300 text-sm">
                {{ t("spendzen.how_it_works.step2.description") }}
              </p>
              <div class="mt-4 text-4xl">🤖</div>
            </div>

            <div
              class="animate-on-scroll relative text-center group"
              style="transition-delay: 200ms"
            >
              <div class="relative z-10 mb-6">
                <div
                  class="inline-flex items-center justify-center w-20 h-20 bg-primary text-white rounded-full text-3xl font-bold shadow-lg shadow-primary/30 group-hover:scale-110 transition-transform"
                >
                  3
                </div>
              </div>
              <div
                class="hidden md:block absolute top-10 left-[60%] w-full h-0.5 bg-gradient-to-r from-primary/50 to-transparent"
              ></div>
              <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-2">
                {{ t("spendzen.how_it_works.step3.title") }}
              </h3>
              <p class="text-gray-600 dark:text-gray-300 text-sm">
                {{ t("spendzen.how_it_works.step3.description") }}
              </p>
              <div class="mt-4 text-4xl">✅</div>
            </div>

            <div
              class="animate-on-scroll relative text-center group"
              style="transition-delay: 300ms"
            >
              <div class="relative z-10 mb-6">
                <div
                  class="inline-flex items-center justify-center w-20 h-20 bg-green-500 text-white rounded-full text-3xl font-bold shadow-lg shadow-green-500/30 group-hover:scale-110 transition-transform"
                >
                  4
                </div>
              </div>
              <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-2">
                {{ t("spendzen.how_it_works.step4.title") }}
              </h3>
              <p class="text-gray-600 dark:text-gray-300 text-sm">
                {{ t("spendzen.how_it_works.step4.description") }}
              </p>
              <div class="mt-4 text-4xl">📧</div>
            </div>
          </div>

          <div class="mt-8 text-center">
            <button
              @click="openSignupModal"
              class="inline-flex items-center gap-2 px-8 py-4 text-base font-semibold text-white btn-gradient rounded-full shadow-lg shadow-primary/40 hover:shadow-xl hover:-translate-y-0.5 transition-all"
            >
              {{ t("spendzen.how_it_works.cta") }}
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </Container>
      </section>

      <!-- Stats Section -->
      <section class="py-6 bg-white dark:bg-gray-900">
        <Container>
          <div class="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div class="animate-on-scroll">
              <div class="text-4xl md:text-5xl font-extrabold text-primary">
                3s
              </div>
              <p class="text-gray-600 dark:text-gray-300 font-medium">
                {{ t("spendzen.stats.scan_time") }}
              </p>
            </div>
            <div class="animate-on-scroll" style="transition-delay: 100ms">
              <div class="text-4xl md:text-5xl font-extrabold text-primary">
                98%
              </div>
              <p class="text-gray-600 dark:text-gray-300 font-medium">
                {{ t("spendzen.stats.ai_precision") }}
              </p>
            </div>
            <div class="animate-on-scroll" style="transition-delay: 200ms">
              <div class="text-4xl md:text-5xl font-extrabold text-primary">
                2h
              </div>
              <p class="text-gray-600 dark:text-gray-300 font-medium">
                {{ t("spendzen.stats.time_saved") }}
              </p>
            </div>
            <div class="animate-on-scroll" style="transition-delay: 300ms">
              <div class="text-4xl md:text-5xl font-extrabold text-primary">
                0
              </div>
              <p class="text-gray-600 dark:text-gray-300 font-medium">
                {{ t("spendzen.stats.no_reentry") }}
              </p>
            </div>
          </div>
        </Container>
      </section>

      <!-- Use Cases Section -->
      <section id="use-cases" class="bg-white dark:bg-gray-900">
        <Container>
          <div class="text-center max-w-2xl mx-auto mb-16">
            <h2
              class="text-3xl md:text-5xl font-extrabold text-gray-900 dark:text-white mb-4 leading-tight"
            >
              {{ t("spendzen.use_cases.title") }}<br />
              <span class="gradient-text">{{
                t("spendzen.use_cases.title_highlight")
              }}</span>
            </h2>
          </div>

          <div class="grid md:grid-cols-3 gap-8">
            <div
              class="animate-on-scroll group bg-gradient-to-br from-primary/5 to-transparent dark:from-primary/10 dark:bg-gray-800/50 p-8 rounded-3xl hover:shadow-xl transition-all"
            >
              <div class="text-5xl mb-6">💼</div>
              <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-3">
                {{ t("spendzen.use_cases.consultants.title") }}
              </h3>
              <p class="text-gray-600 dark:text-gray-300 mb-4">
                {{ t("spendzen.use_cases.consultants.description") }}
              </p>
              <ul class="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <li class="flex items-center gap-2">
                  <span class="text-primary">✓</span>
                  {{ t("spendzen.use_cases.consultants.feature1") }}
                </li>
                <li class="flex items-center gap-2">
                  <span class="text-primary">✓</span>
                  {{ t("spendzen.use_cases.consultants.feature2") }}
                </li>
                <li class="flex items-center gap-2">
                  <span class="text-primary">✓</span>
                  {{ t("spendzen.use_cases.consultants.feature3") }}
                </li>
              </ul>
            </div>

            <div
              class="animate-on-scroll group bg-gradient-to-br from-secondary/5 to-transparent dark:from-secondary/10 dark:bg-gray-800/50 p-8 rounded-3xl hover:shadow-xl transition-all"
              style="transition-delay: 100ms"
            >
              <div class="text-5xl mb-6">🏢</div>
              <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-3">
                {{ t("spendzen.use_cases.pme.title") }}
              </h3>
              <p class="text-gray-600 dark:text-gray-300 mb-4">
                {{ t("spendzen.use_cases.pme.description") }}
              </p>
              <ul class="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <li class="flex items-center gap-2">
                  <span class="text-primary">✓</span>
                  {{ t("spendzen.use_cases.pme.feature1") }}
                </li>
                <li class="flex items-center gap-2">
                  <span class="text-primary">✓</span>
                  {{ t("spendzen.use_cases.pme.feature2") }}
                </li>
                <li class="flex items-center gap-2">
                  <span class="text-primary">✓</span>
                  {{ t("spendzen.use_cases.pme.feature3") }}
                </li>
              </ul>
            </div>

            <div
              class="animate-on-scroll group bg-gradient-to-br from-primary/5 to-transparent dark:from-primary/10 dark:bg-gray-800/50 p-8 rounded-3xl hover:shadow-xl transition-all"
              style="transition-delay: 200ms"
            >
              <div class="text-5xl mb-6">👔</div>
              <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-3">
                {{ t("spendzen.use_cases.freelance.title") }}
              </h3>
              <p class="text-gray-600 dark:text-gray-300 mb-4">
                {{ t("spendzen.use_cases.freelance.description") }}
              </p>
              <ul class="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <li class="flex items-center gap-2">
                  <span class="text-primary">✓</span>
                  {{ t("spendzen.use_cases.freelance.feature1") }}
                </li>
                <li class="flex items-center gap-2">
                  <span class="text-primary">✓</span>
                  {{ t("spendzen.use_cases.freelance.feature2") }}
                </li>
                <li class="flex items-center gap-2">
                  <span class="text-primary">✓</span>
                  {{ t("spendzen.use_cases.freelance.feature3") }}
                </li>
              </ul>
            </div>
          </div>
        </Container>
      </section>

      <!-- Pricing Section -->
      <section id="pricing" class="bg-white dark:bg-gray-900">
        <Container>
          <div class="text-center max-w-2xl mx-auto mb-16">
            <h2
              class="text-3xl md:text-5xl font-extrabold text-gray-900 dark:text-white mb-4 leading-tight"
            >
              {{ t("spendzen.pricing.title") }}<br />
              <span class="gradient-text">{{
                t("spendzen.pricing.title_highlight")
              }}</span>
            </h2>
            <p class="text-lg text-gray-500 dark:text-gray-400">
              {{ t("spendzen.pricing.subtitle") }}
            </p>
          </div>

          <div class="grid md:grid-cols-3 gap-8 items-center">
            <!-- Free Plan -->
            <div
              class="animate-on-scroll bg-white dark:bg-gray-800 rounded-3xl p-8 border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-all"
            >
              <div class="text-center mb-8">
                <h3
                  class="text-xl font-bold text-gray-900 dark:text-white mb-2"
                >
                  {{ t("spendzen.pricing.free.name") }}
                </h3>
                <div class="flex items-baseline justify-center gap-1">
                  <span
                    class="text-4xl font-extrabold text-gray-900 dark:text-white"
                    >{{ formatValueWithCurrency(0) }}</span
                  >
                  <span class="text-gray-500 dark:text-gray-400">{{
                    t("spendzen.pricing.per_month")
                  }}</span>
                </div>
                <p class="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  {{ t("spendzen.pricing.free.description") }}
                </p>
              </div>
              <ul class="space-y-4 mb-8">
                <li class="flex items-center gap-3">
                  <span
                    class="flex items-center justify-center w-5 h-5 bg-primary/10 text-primary text-xs rounded-full"
                    >✓</span
                  >
                  <span class="text-gray-700 dark:text-gray-300"
                    ><strong>{{ t("spendzen.pricing.free.feature1") }}</strong
                    >{{ t("spendzen.pricing.free.feature1_suffix") }}</span
                  >
                </li>
                <li class="flex items-center gap-3">
                  <span
                    class="flex items-center justify-center w-5 h-5 bg-primary/10 text-primary text-xs rounded-full"
                    >✓</span
                  >
                  <span class="text-gray-700 dark:text-gray-300">{{
                    t("spendzen.pricing.free.feature2")
                  }}</span>
                </li>
                <li class="flex items-center gap-3">
                  <span
                    class="flex items-center justify-center w-5 h-5 bg-primary/10 text-primary text-xs rounded-full"
                    >✓</span
                  >
                  <span class="text-gray-700 dark:text-gray-300">{{
                    t("spendzen.pricing.free.feature3")
                  }}</span>
                </li>
                <li class="flex items-center gap-3">
                  <span
                    class="flex items-center justify-center w-5 h-5 bg-primary/10 text-primary text-xs rounded-full"
                    >✓</span
                  >
                  <span class="text-gray-700 dark:text-gray-300">{{
                    t("spendzen.pricing.free.feature4")
                  }}</span>
                </li>
              </ul>
              <button
                @click="openSignupModal"
                class="block w-full py-3 text-center font-semibold text-primary border-2 border-primary rounded-full hover:bg-primary hover:text-white transition-all"
              >
                {{ t("spendzen.pricing.free.cta") }}
              </button>
            </div>

            <!-- Unlimited Plan -->
            <div
              class="animate-on-scroll pricing-popular text-white rounded-3xl p-8 shadow-2xl relative"
              style="transition-delay: 100ms"
            >
              <div
                class="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-secondary text-white text-sm font-semibold rounded-full whitespace-nowrap shadow-lg"
              >
                ⭐ {{ t("spendzen.pricing.unlimited.badge") }}
              </div>
              <div class="text-center mb-8">
                <h3 class="text-xl font-bold mb-2">
                  {{ t("spendzen.pricing.unlimited.name") }}
                </h3>
                <div class="flex items-baseline justify-center gap-1">
                  <span class="text-4xl font-extrabold">{{
                    formatValueWithCurrency(49)
                  }}</span>
                  <span class="text-white/80">{{
                    t("spendzen.pricing.per_month")
                  }}</span>
                </div>
                <p class="text-sm text-white/80 mt-2">
                  {{ t("spendzen.pricing.unlimited.description") }}
                </p>
              </div>
              <ul class="space-y-4 mb-8">
                <li class="flex items-center gap-3">
                  <span
                    class="flex items-center justify-center w-5 h-5 bg-white/20 text-white text-xs rounded-full"
                    >✓</span
                  >
                  <span
                    ><strong>{{
                      t("spendzen.pricing.unlimited.feature1")
                    }}</strong></span
                  >
                </li>
                <li class="flex items-center gap-3">
                  <span
                    class="flex items-center justify-center w-5 h-5 bg-white/20 text-white text-xs rounded-full"
                    >✓</span
                  >
                  <span>{{ t("spendzen.pricing.unlimited.feature2") }}</span>
                </li>
                <li class="flex items-center gap-3">
                  <span
                    class="flex items-center justify-center w-5 h-5 bg-white/20 text-white text-xs rounded-full"
                    >✓</span
                  >
                  <span>{{ t("spendzen.pricing.unlimited.feature3") }}</span>
                </li>
                <li class="flex items-center gap-3">
                  <span
                    class="flex items-center justify-center w-5 h-5 bg-white/20 text-white text-xs rounded-full"
                    >✓</span
                  >
                  <span
                    ><strong>{{
                      t("spendzen.pricing.unlimited.feature4")
                    }}</strong></span
                  >
                </li>
                <li class="flex items-center gap-3">
                  <span
                    class="flex items-center justify-center w-5 h-5 bg-white/20 text-white text-xs rounded-full"
                    >✓</span
                  >
                  <span>{{ t("spendzen.pricing.unlimited.feature5") }}</span>
                </li>
                <li class="flex items-center gap-3">
                  <span
                    class="flex items-center justify-center w-5 h-5 bg-white/20 text-white text-xs rounded-full"
                    >✓</span
                  >
                  <span>{{ t("spendzen.pricing.unlimited.feature6") }}</span>
                </li>
              </ul>
              <button
                @click="openSignupModal"
                class="block w-full py-3 text-center font-semibold bg-white text-primary rounded-full hover:bg-gray-50 transition-all font-geologica"
              >
                {{ t("spendzen.pricing.unlimited.cta") }}
              </button>
            </div>

            <!-- KANZen Plan -->
            <div
              class="animate-on-scroll bg-white dark:bg-gray-800 rounded-3xl p-8 border-2 border-primary/30 hover:shadow-xl transition-all relative overflow-hidden"
              style="transition-delay: 200ms"
            >
              <div
                class="absolute top-0 right-0 w-20 h-20 bg-primary/5 rounded-bl-full"
              ></div>
              <div class="text-center mb-8">
                <h3
                  class="text-xl font-bold text-gray-900 dark:text-white mb-2"
                >
                  {{ t("spendzen.pricing.kanzen.name") }}
                </h3>
                <div class="flex items-baseline justify-center gap-1">
                  <span class="text-4xl font-extrabold text-primary">{{
                    t("spendzen.pricing.kanzen.included")
                  }}</span>
                </div>
                <p class="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  {{ t("spendzen.pricing.kanzen.description") }}
                </p>
              </div>
              <ul class="space-y-4 mb-8">
                <li class="flex items-center gap-3">
                  <span
                    class="flex items-center justify-center w-5 h-5 bg-primary/10 text-primary text-xs rounded-full"
                    >✓</span
                  >
                  <span class="text-gray-700 dark:text-gray-300"
                    ><strong>{{
                      t("spendzen.pricing.kanzen.feature1")
                    }}</strong></span
                  >
                </li>
                <li class="flex items-center gap-3">
                  <span
                    class="flex items-center justify-center w-5 h-5 bg-primary/10 text-primary text-xs rounded-full"
                    >✓</span
                  >
                  <span class="text-gray-700 dark:text-gray-300">{{
                    t("spendzen.pricing.kanzen.feature2")
                  }}</span>
                </li>
                <li class="flex items-center gap-3">
                  <span
                    class="flex items-center justify-center w-5 h-5 bg-primary/10 text-primary text-xs rounded-full"
                    >✓</span
                  >
                  <span class="text-gray-700 dark:text-gray-300">{{
                    t("spendzen.pricing.kanzen.feature3")
                  }}</span>
                </li>
                <li class="flex items-center gap-3">
                  <span
                    class="flex items-center justify-center w-5 h-5 bg-primary/10 text-primary text-xs rounded-full"
                    >✓</span
                  >
                  <span class="text-gray-700 dark:text-gray-300">{{
                    t("spendzen.pricing.kanzen.feature4")
                  }}</span>
                </li>
                <li class="flex items-center gap-3">
                  <span
                    class="flex items-center justify-center w-5 h-5 bg-primary/10 text-primary text-xs rounded-full"
                    >✓</span
                  >
                  <span class="text-gray-700 dark:text-gray-300">{{
                    t("spendzen.pricing.kanzen.feature5")
                  }}</span>
                </li>
              </ul>
              <NuxtLink
                to="#contact-section"
                class="block w-full py-3 text-center font-semibold text-primary border-2 border-primary rounded-full hover:bg-primary hover:text-white transition-all font-geologica"
              >
                {{ t("spendzen.pricing.kanzen.cta") }}
              </NuxtLink>
            </div>
          </div>
        </Container>
      </section>

      <!-- KANZen Integration Section -->
      <section id="kanzen" class="bg-white dark:bg-gray-900">
        <Container>
          <div
            class="animate-on-scroll grid lg:grid-cols-2 gap-12 lg:gap-16 items-center"
          >
            <div>
              <h2
                class="text-3xl md:text-5xl font-extrabold text-gray-900 dark:text-white mb-4 leading-tight"
              >
                {{ t("spendzen.kanzen_integration.title") }}<br />
                <span class="gradient-text">{{
                  t("spendzen.kanzen_integration.title_highlight")
                }}</span>
              </h2>
              <p class="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                {{ t("spendzen.kanzen_integration.description") }}
                <strong>{{
                  t("spendzen.kanzen_integration.description_kanzen")
                }}</strong
                >{{ t("spendzen.kanzen_integration.description_end") }}
              </p>

              <div class="space-y-4 mb-8">
                <div
                  class="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl"
                >
                  <span class="text-2xl">🔄</span>
                  <div>
                    <h4 class="font-semibold text-gray-900 dark:text-white">
                      {{ t("spendzen.kanzen_integration.sync.title") }}
                    </h4>
                    <p class="text-sm text-gray-600 dark:text-gray-300">
                      {{ t("spendzen.kanzen_integration.sync.description") }}
                    </p>
                  </div>
                </div>
                <div
                  class="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl"
                >
                  <span class="text-2xl">✅</span>
                  <div>
                    <h4 class="font-semibold text-gray-900 dark:text-white">
                      {{ t("spendzen.kanzen_integration.workflows.title") }}
                    </h4>
                    <p class="text-sm text-gray-600 dark:text-gray-300">
                      {{
                        t("spendzen.kanzen_integration.workflows.description")
                      }}
                    </p>
                  </div>
                </div>
                <div
                  class="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl"
                >
                  <span class="text-2xl">📊</span>
                  <div>
                    <h4 class="font-semibold text-gray-900 dark:text-white">
                      {{ t("spendzen.kanzen_integration.accounting.title") }}
                    </h4>
                    <p class="text-sm text-gray-600 dark:text-gray-300">
                      {{
                        t("spendzen.kanzen_integration.accounting.description")
                      }}
                    </p>
                  </div>
                </div>
              </div>

              <NuxtLink
                to="#contact-section"
                class="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-white btn-gradient rounded-full shadow-lg shadow-primary/40 hover:shadow-xl hover:shadow-primary/50 hover:-translate-y-0.5 transition-all font-geologica"
              >
                {{ t("spendzen.kanzen_integration.cta") }}
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </NuxtLink>
            </div>

            <!-- Integration Diagram -->
            <div class="flex justify-center">
              <div class="relative w-80 h-80">
                <!-- Center - KANZen -->
                <div
                  class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-28 h-28 bg-gray-900 rounded-full flex items-center justify-center text-white font-bold text-lg text-center shadow-2xl z-10"
                >
                  {{ t("spendzen.kanzen_integration.description_kanzen") }}
                </div>
                <!-- SPENDZen -->
                <div
                  class="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-20 btn-gradient rounded-2xl flex items-center justify-center text-white text-xs font-bold shadow-lg hover:scale-105 transition-all z-10 animate-pulse-slow"
                >
                  SPENDZen
                </div>
                <!-- Other modules -->
                <div
                  class="absolute top-1/2 right-0 -translate-y-1/2 w-16 h-16 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl flex items-center justify-center text-xs font-semibold text-gray-700 dark:text-gray-300 shadow-md z-10"
                >
                  {{ t("spendzen.kanzen_integration.modules.hr") }}
                </div>
                <div
                  class="absolute bottom-0 left-1/2 -translate-x-1/2 w-16 h-16 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl flex items-center justify-center text-xs font-semibold text-gray-700 dark:text-gray-300 shadow-md z-10"
                >
                  {{ t("spendzen.kanzen_integration.modules.accounting") }}
                </div>
                <div
                  class="absolute top-1/2 left-0 -translate-y-1/2 w-16 h-16 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl flex items-center justify-center text-xs font-semibold text-gray-700 dark:text-gray-300 shadow-md z-10"
                >
                  {{ t("spendzen.kanzen_integration.modules.crm") }}
                </div>
                <!-- Lines -->
                <div
                  class="absolute top-20 left-1/2 -translate-x-1/2 w-0.5 h-14 bg-primary/30"
                ></div>
                <div
                  class="absolute top-1/2 right-16 -translate-y-1/2 w-14 h-0.5 bg-gray-200"
                ></div>
                <div
                  class="absolute bottom-20 left-1/2 -translate-x-1/2 w-0.5 h-14 bg-gray-200"
                ></div>
                <div
                  class="absolute top-1/2 left-16 -translate-y-1/2 w-14 h-0.5 bg-gray-200"
                ></div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      <!-- Download / CTA Section -->
      <section id="download" class="bg-white dark:bg-gray-900">
        <Container>
          <div
            class="relative p-10 lg:p-16 btn-gradient rounded-3xl overflow-hidden text-center"
          >
            <!-- Content -->
            <div class="relative z-10">
              <h2 class="text-3xl md:text-5xl font-extrabold text-white mb-4">
                {{ t("spendzen.download.title") }}
                <span class="text-white/80">{{
                  t("spendzen.download.title_highlight")
                }}</span>
              </h2>
              <p class="text-white/90 mb-8 max-w-lg mx-auto">
                {{ t("spendzen.download.description") }}
              </p>

              <!-- App Store Buttons -->
              <div class="flex flex-wrap justify-center gap-4 mb-6">
                <a
                  href="javascript:void(0)"
                  class="inline-flex items-center gap-3 px-6 py-4 bg-black text-white rounded-xl hover:bg-gray-800 transition-all shadow-lg"
                >
                  <svg class="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                    <path
                      d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"
                    />
                  </svg>
                  <div class="text-left">
                    <div class="text-xs opacity-80">
                      {{ t("spendzen.download.download_on") }}
                    </div>
                    <div class="text-sm font-semibold">App Store</div>
                  </div>
                </a>
                <a
                  href="javascript:void(0)"
                  class="inline-flex items-center gap-3 px-6 py-4 bg-black text-white rounded-xl hover:bg-gray-800 transition-all shadow-lg"
                >
                  <svg class="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                    <path
                      d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"
                    />
                  </svg>
                  <div class="text-left">
                    <div class="text-xs opacity-80">
                      {{ t("spendzen.download.available_on") }}
                    </div>
                    <div class="text-sm font-semibold">Google Play</div>
                  </div>
                </a>
              </div>

              <p class="text-white/70 text-sm mb-6">
                {{ t("spendzen.download.or") }}
              </p>

              <div id="demo" class="max-w-md mx-auto">
                <AppButton
                  @click="openSignupModal"
                  variant="white"
                  size="lg"
                  :text="t('spendzen.download.create_account')"
                  icon="ph:user-plus-bold"
                />
              </div>

              <p class="mt-6 text-sm text-white/80">
                {{ t("spendzen.download.benefits") }}
              </p>
            </div>
            <!-- Decorations -->
            <div
              class="absolute top-0 right-0 w-72 h-72 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/3"
            ></div>
            <div
              class="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/3"
            ></div>
          </div>
        </Container>
      </section>

      <section id="reviews" class="py-16 bg-white dark:bg-gray-900">
        <Container>
          <div class="mb-12 text-center space-y-4">
            <h2
              class="text-3xl md:text-5xl font-extrabold text-gray-900 dark:text-white mb-4 leading-tight motion-opacity-0 motion-scale-in-90 intersect:motion-opacity-in-0 intersect-once"
            >
              {{ t("spendzen.testimonials.title") }}<br />
              <span class="gradient-text">{{
                t("spendzen.testimonials.title_highlight")
              }}</span>
            </h2>
            <p
              class="text-base md:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto motion-opacity-0 motion-scale-in-90 intersect:motion-opacity-in-0 intersect-once"
            >
              {{ t("spendzen.testimonials.subtitle") }}
            </p>
          </div>

          <div class="flex justify-center">
            <div class="relative w-full px-4">
              <div
                class="overflow-hidden w-full relative h-full"
                @touchstart="handleTestimonialTouchStart"
                @touchend="handleTestimonialTouchEnd"
              >
                <div
                  class="flex transition-transform duration-500 ease-in-out"
                  :style="{
                    transform: `translateX(-${currentTestimonialPage * 100}%)`,
                  }"
                >
                  <div
                    v-for="(page, pageIndex) in testimonialPages"
                    :key="pageIndex"
                    class="flex-shrink-0 flex w-full gap-6 justify-center"
                  >
                    <div
                      v-for="testimonial in page"
                      :key="testimonial.author"
                      class="w-full sm:w-1/2 lg:w-1/3 flex"
                    >
                      <div
                        class="bg-white dark:bg-gray-800 rounded-xl p-6 shadow border border-gray-200 dark:border-white/10 flex flex-col justify-between h-full transition duration-300 w-full"
                      >
                        <!-- Stars -->
                        <div class="flex gap-1 text-yellow-400 mb-4">
                          <span v-for="i in 5" :key="i">★</span>
                        </div>

                        <!-- Content -->
                        <div
                          class="text-gray-800 dark:text-gray-100 text-sm leading-relaxed line-clamp-5 mb-4"
                        >
                          "{{ testimonial.text }}"
                        </div>

                        <!-- Footer -->
                        <div
                          class="flex items-center gap-4 mt-auto pt-4 border-t border-gray-100 dark:border-white/10"
                        >
                          <div
                            class="w-10 h-10 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-white font-semibold text-sm"
                          >
                            {{ testimonial.initials }}
                          </div>
                          <div>
                            <p
                              class="font-semibold text-gray-900 dark:text-white text-sm"
                            >
                              {{ testimonial.author }}
                            </p>
                            <p class="text-xs text-gray-500 dark:text-gray-400">
                              {{ testimonial.role }}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div
                v-if="totalTestimonialPages > 1"
                class="flex justify-between items-center mt-6 px-6"
              >
                <button
                  @click="prevTestimonialPage"
                  class="bg-white/80 dark:bg-gray-900/80 text-gray-700 dark:text-white p-1 rounded-full border border-gray-200 dark:border-white/10 shadow hover:scale-110 transition"
                  aria-label="Previous"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-5 h-5"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                <div class="flex justify-center gap-2">
                  <span
                    v-for="(page, index) in totalTestimonialPages"
                    :key="index"
                    class="w-2 h-2 rounded-full"
                    :class="{
                      'bg-[#00A17A]': currentTestimonialPage === index,
                      'bg-gray-300 dark:bg-gray-600':
                        currentTestimonialPage !== index,
                    }"
                  ></span>
                </div>

                <button
                  @click="nextTestimonialPage"
                  class="bg-white/80 dark:bg-gray-900/80 text-gray-700 dark:text-white p-1 rounded-full border border-gray-200 dark:border-white/10 shadow hover:scale-110 transition"
                  aria-label="Next"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-5 h-5"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </Container>
      </section>

      <!-- FAQ Section -->
      <section id="faq" class="bg-white dark:bg-gray-900">
        <Container>
          <div class="text-center max-w-2xl mx-auto mb-16">
            <h2
              class="text-3xl md:text-5xl font-extrabold text-gray-900 dark:text-white mb-4 leading-tight"
            >
              {{ t("spendzen.faq.title") }}<br />
              <span class="gradient-text">{{
                t("spendzen.faq.title_highlight")
              }}</span>
            </h2>
          </div>

          <div class="mt-16">
            <AppFaq :items="faqsList">
              <!-- Custom answer for Currency -->
              <template #answer-0>
                <p>
                  {{
                    t("spendzen.faq.q1.answer", { currency: currencySymbol })
                  }}
                </p>
              </template>

              <!-- Custom answer for Security/Location -->
              <template #answer-5>
                <p>
                  {{ t("spendzen.faq.q6.answer_prefix") }}
                  <strong
                    >{{ t("security.hosted_in") }}
                    {{ t(countryNameKey) }}</strong
                  >
                  {{ t("spendzen.faq.q6.answer_suffix") }}
                  {{ t(countryAdjKey) }}
                  <span v-if="isSwiss">{{
                    t("spendzen.faq.q6.based_in_geneva")
                  }}</span
                  >.
                </p>
              </template>
            </AppFaq>
          </div>
        </Container>
      </section>

      <section id="contact-section" class="scroll-mt-[130px]">
        <Container>
          <div
            class="relative text-white shadow-lg overflow-hidden bg-primary rounded-3xl flex flex-col lg:flex-row motion-opacity-0 motion-scale-in-90 motion-delay-200 intersect:motion-opacity-in-0"
          >
            <ContactForm highlightClass="gradient-text-light" />
          </div>
        </Container>
      </section>

      <!-- Footer removed in favor of AppFooter -->
    </main>
  </div>

  <!-- Signup Modal -->
  <div v-if="isSignupModalOpen" class="fixed inset-0 z-[100]">
    <!-- Backdrop -->
    <div
      class="absolute inset-0 bg-black/50 backdrop-blur-sm"
      @click="closeSignupModal"
    ></div>

    <!-- Modal Content -->
    <div
      class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md mx-4"
    >
      <div
        class="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl overflow-hidden"
      >
        <!-- Step 1: Email Form -->
        <div v-if="signupStep === 1" class="p-8">
          <button
            @click="closeSignupModal"
            class="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:text-gray-300"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          <div class="text-center mb-6">
            <div
              class="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4"
            >
              <span class="text-3xl">🚀</span>
            </div>
            <h3 class="text-2xl font-bold text-gray-900 dark:text-white">
              Créer votre compte gratuit
            </h3>
            <p class="text-gray-600 dark:text-gray-300 mt-2">
              10 scans gratuits par mois, sans engagement
            </p>
          </div>

          <form @submit.prevent="handleSignupSubmit" class="space-y-4">
            <div>
              <label
                for="signup-email"
                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >Email professionnel</label
              >
              <input
                type="email"
                id="signup-email"
                v-model="signupEmail"
                required
                placeholder="votre@email.com"
                class="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-xl focus:outline-none focus:border-primary transition-all"
              />
            </div>

            <button
              type="submit"
              class="w-full py-4 btn-gradient text-white font-semibold rounded-xl shadow-lg shadow-primary/30 hover:shadow-xl transition-all flex items-center justify-center gap-2"
            >
              <span>Recevoir le lien de vérification</span>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
          </form>

          <div class="mt-6 text-center">
            <p class="text-xs text-gray-500 dark:text-gray-400">
              {{ t("spendzen.signup_modal.terms_prefix") }}
              <NuxtLink
                :to="localePath('/cgv')"
                target="_blank"
                class="text-primary hover:underline"
                >{{ t("footer.cgv") }}</NuxtLink
              >,
              <NuxtLink
                :to="localePath('/cgu')"
                target="_blank"
                class="text-primary hover:underline"
                >{{ t("footer.cgu") }}</NuxtLink
              >
              {{ t("spendzen.signup_modal.terms_and") }}
              <NuxtLink
                :to="localePath('/privacy')"
                target="_blank"
                class="text-primary hover:underline"
                >{{ t("footer.privacy") }}</NuxtLink
              >.
            </p>
          </div>

          <div class="mt-6 pt-6 border-t border-gray-100 text-center">
            <p class="text-sm text-gray-600 dark:text-gray-300">
              {{ t("spendzen.signup_modal.already_registered") }}
              <a
                href="javascript:void(0)"
                class="text-primary font-medium hover:underline"
                >{{ t("spendzen.signup_modal.login") }}</a
              >
            </p>
          </div>
        </div>

        <!-- Step 2: Email Sent Confirmation -->
        <div v-else class="p-8">
          <div class="text-center">
            <div
              class="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6"
            >
              <svg
                class="w-10 h-10 text-green-600"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h3 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {{ t("spendzen.signup_modal.success_title") }}
            </h3>
            <p class="text-gray-600 dark:text-gray-300 mb-2">
              {{ t("spendzen.signup_modal.success_subtitle") }}
            </p>
            <p class="font-semibold text-primary text-lg mb-6">
              {{ signupEmail }}
            </p>

            <div class="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 mb-6">
              <p class="text-sm text-gray-600 dark:text-gray-300">
                <strong>{{
                  t("spendzen.signup_modal.success_instruction")
                }}</strong>
                {{ t("spendzen.signup_modal.success_instruction_suffix") }}
              </p>
            </div>

            <div class="space-y-3">
              <p class="text-sm text-gray-500 dark:text-gray-400">
                {{ t("spendzen.signup_modal.not_received") }}
              </p>
              <button
                @click="resendEmail"
                class="text-primary font-medium hover:underline text-sm"
              >
                {{ t("spendzen.signup_modal.resend") }}
              </button>
            </div>

            <button
              @click="closeSignupModal"
              class="mt-6 text-gray-500 dark:text-gray-400 hover:text-gray-700 text-sm"
            >
              {{ t("spendzen.signup_modal.close") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Custom styles that complement Tailwind */

.btn-gradient {
  background: linear-gradient(135deg, #00a0a0 0%, #007f80 100%);
}

.shadow-glass {
  box-shadow: 0 8px 32px rgba(0, 160, 160, 0.15);
}

.phone-shadow {
  box-shadow:
    0 25px 50px -12px rgba(0, 0, 0, 0.25),
    inset 0 0 0 1px rgba(255, 255, 255, 0.1);
}

/* Intersection Observer animations */
.animate-on-scroll {
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.6s ease-out;
}

.animate-on-scroll.animate-in {
  opacity: 1;
  transform: translateY(0);
}

.animate-slide-left {
  opacity: 0;
  transform: translateX(-30px);
  transition: all 0.6s ease-out;
}

.animate-slide-left.animate-in {
  opacity: 1;
  transform: translateX(0);
}

/* Pricing card highlight */
.pricing-popular {
  background: linear-gradient(135deg, #00a0a0 0%, #007f80 100%);
  transform: scale(1.05);
}
</style>
